import React from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { GlobalContext } from 'components/context'
import { currentActiveTypeFromKeys, statusIsInactive } from 'helpers'
import { featureEnabled, featureExcluded } from 'components/features'

// TODO: Should consider sanitizing first
//  when using dangerouslySetInnerHTML
//  https://www.npmjs.com/package/sanitize-html
// NOTE: we are transitioning away from pages.memebership.access_chip to pages.membership.access
//   with separate phrase values for each key type where appropriate.
//   While transitioning, we need to support both sets of translations
const Info = ({ currKey, t, nfcEnabled = false }) => {
  const phraseBase = `pages.membership.${nfcEnabled ? 'access' : 'access_chip'}`

  if (!currKey) {
    return (
      <div>
        { t(`${phraseBase}.no_access`) }
      </div>
    )
  }

  const phraseKey = `pages.membership.${nfcEnabled ? 'access.' + currKey.type : 'access_chip'}`

  const vars = (key, t) => {
    const statusMap = {
      active: 'activated',
      inactive: 'deactivated',
      suspended: 'suspended',
    }

    const statusTxt = t(`${phraseBase}.${statusMap[key.status]}`)

    const data = {
      status: `<span class="${key.status}">${statusTxt}</span>`,
    }
    if (key.type === 'rfid' || key.type === 'pin') {
      data.code = `<strong>${key.code}</strong>`
    }

    return data
  }

  const txt = t(`${phraseKey}.info`, vars(currKey, t))

  return (
    <div
      className={'chip'}
      dangerouslySetInnerHTML={{ __html: txt }}
    />
  )
}

Info.propTypes = {
  currKey: PropTypes.object,
  t: PropTypes.func.isRequired,
  nfcEnabled: PropTypes.bool,
}

const LostLink = ({ currKey, match, t, nfcEnabled = false }) => {
  if (!currKey) return null
  if (currKey.type !== 'rfid') return null

  const phraseBase = `pages.membership.${nfcEnabled ? 'access.' + currKey.type : 'access_chip'}`

  const path = 'access/lost_chip'
  const txt = `${phraseBase}.lost`

  return (
    <React.Fragment>
      <Link to={`${match.path}/${path}`} data-cy={path}> { t(txt) } </Link>
    </React.Fragment>
  )
}

LostLink.propTypes = {
  currKey: PropTypes.object,
  match: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  nfcEnabled: PropTypes.bool,
}

// TODO: remove nfcEnabled parameter when all operators have NFC enabled
const ActivateLink = (type, match, t, nfcEnabled = false) => {
  let path = 'access'
  if (type === 'nfc') {
    path += '/nfc'
  } else if (type === 'rfid') {
    path += '/rfid'
  }
  const phraseBase = `pages.membership.${nfcEnabled
    ? 'access.' + type
    : 'access_chip'}.activate`
  const txt = type ? `${phraseBase}.activate` : 'pages.membership.access_chip.activate'

  return (
    <React.Fragment>
      <Link to={{ pathname: `${match.path}/${path}`, search: `?action=activate&type=${type}` }} data-cy={path}>
        { t(txt) }
      </Link>
    </React.Fragment>
  )
}

ActivateLink.propTypes = {
  type: PropTypes.string,
  match: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  nfcEnabled: PropTypes.bool,
}

const SyncLink = (match, t) => {
  const path = 'access/nfc'
  const txt = 'pages.membership.access.nfc.sync'

  return (
    <p>
      <Link to={{ pathname: `${match.path}/${path}`, search: '?action=sync' }} data-cy={path}> { t(txt) } </Link>
    </p>
  )
}

SyncLink.propTypes = {
  match: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
}

const SwitchLink = (currKey, match, t) => {
  if (!currKey) return null
  if (currKey.type !== 'rfid' && currKey.type !== 'nfc') return null

  const keySwitch = {
    rfid: 'NFC',
    nfc: 'RFID',
  }

  let path = 'access'
  if (currKey.type === 'nfc') {
    path += '/rfid'
  } else if (currKey.type === 'rfid') {
    path += '/nfc'
  }

  const txt = `pages.membership.access.switch.from_${currKey.type}`

  return (
    <p>
      <Link className={`switch ${keySwitch[currKey.type]}`} to={{ pathname: `${match.path}/${path}`, search: '?action=switch' }} data-cy={path}> { t(txt) } </Link>
    </p>
  )
}

SwitchLink.propTypes = {
  currKey: PropTypes.object,
  match: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
}

// My membership access links for when nfc is enabled
const ChooseLinks = ({ rfid, nfc, match, t, nfcEnabled = false }) => {
  const hasRfid = rfid?.type === 'rfid'
  const hasNfc = nfc?.type === 'nfc'

  if (!nfcEnabled) {
    if (hasRfid) return null

    return (
      <React.Fragment>
        { ActivateLink('rfid', match, t) }
      </React.Fragment>
    )
  }

  // shouldn't happen, but might for trial phase - TODO: decide what to do in this scenario
  if (hasRfid && hasNfc) return null

  if (hasRfid && !hasNfc) return SwitchLink(rfid, match, t)
  if (!hasRfid && hasNfc) {
    return (
      <React.Fragment>
        { SyncLink(match, t) }
        { SwitchLink(nfc, match, t) }
      </React.Fragment>
    )
  }

  if (!hasRfid && !hasNfc) {
    return (
      <React.Fragment>
        <h3>{t('pages.membership.access.choose')}</h3>
        <ol>
          <li>{ ActivateLink('nfc', match, t, nfcEnabled) }</li>
          <li>{ ActivateLink('rfid', match, t, nfcEnabled) }</li>
        </ol>
      </React.Fragment>
    )
  } else {
    return (
      <React.Fragment>
        { ActivateLink('rfid', match, t) }
      </React.Fragment>
    )
  }
}

ChooseLinks.propTypes = {
  rfid: PropTypes.object,
  nfc: PropTypes.object,
  match: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
}

const AccessMethod = ({ match, membership }) => {
  const { t, locale, env } = React.useContext(GlobalContext)
  const nfcEnabled = featureEnabled('nfc', locale, env)
  const excluded = featureExcluded('nfc', 'location', membership.location.name)

  if (!membership) return null
  if (statusIsInactive(membership.status)) return null

  const rfid = currentActiveTypeFromKeys(membership.keys, 'rfid', true)
  const nfc = currentActiveTypeFromKeys(membership.keys, 'nfc', true)

  // My membership access links for when nfc is not enabled
  if (!nfcEnabled || excluded) {
    return (
      <div id='access-chip' className='chip'>
        <h2>{ t('pages.membership.access_chip.title') }</h2>
        <Info currKey={rfid} t={t} />
        <div className='link'>
          {
            typeof rfid === 'object'
              ? <LostLink currKey={rfid} t={t} match={match} nfcEnabled={nfcEnabled} />
              : <Link to={'membership/activate-chip'}>{t('pages.activate_chip.button')}</Link>
          }
        </div>
        {
          excluded &&
            <div className='excluded'>
              <p className='warn'>{t('pages.membership.access.nfc.temporarily_unavailable.header')}</p>
              <p>{t('pages.membership.access.nfc.temporarily_unavailable.text', { name: membership.location.name })}</p>
            </div>
        }
      </div>
    )
  }

  return (
    <div id='access-chip' className='choose'>
      <h2>{ t('pages.membership.access.title') }</h2>
      <Info currKey={nfc || rfid} t={t} nfcEnabled={nfcEnabled} />
      <ChooseLinks rfid={rfid} nfc={nfc} match={match} t={t} nfcEnabled={nfcEnabled} />
      {typeof rfid === 'object' && <LostLink currKey={rfid} t={t} match={match} nfcEnabled={nfcEnabled} />}
    </div>
  )
}

AccessMethod.propTypes = {
  match: PropTypes.object.isRequired,
  membership: PropTypes.object,
}

export default AccessMethod
